import { useState } from "react";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Phone,
  User,
  Stethoscope,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { staggerContainer, fadeUp, fadeRight } from "../animations/variants";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function AppointmentSection() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: "",
    date: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState("");

  const handle = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    emailjs
      .send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          phone: form.phone,
          date: form.date || "Not specified",
          notes: form.notes || "None",
        },
        EMAILJS_PUBLIC_KEY,
      )
      .then(() => {
        setLoading(false);
        setSubmitted(true);
      })
      .catch(() => {
        setLoading(false);
        setError("Failed to send. Please call us directly or try again.");
      });
  };

  const inputClass = (field) =>
    `w-full border-2 rounded-xl px-4 py-3.5 text-sm text-gray-700 outline-none transition-all duration-300 bg-white ${
      focused === field
        ? "border-primary-500 shadow-md shadow-primary-100"
        : "border-gray-200 hover:border-gray-300"
    }`;

  return (
    <section
      id="appointment"
      className="py-24 bg-gradient-to-b from-gray-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left info */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
          >
            <motion.p variants={fadeUp} className="section-subtitle">
              Get Scanned
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="section-title text-gray-900 mb-5"
            >
              Book Your <span className="gradient-text">Appointment</span>
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="text-gray-500 text-lg leading-relaxed mb-10"
            >
              Schedule your CT Scan or MRI with ease. Our team will confirm your
              slot and guide you through the preparation.
            </motion.p>

            {/* Info cards */}
            <motion.div variants={staggerContainer} className="space-y-4">
              {[
                {
                  icon: Phone,
                  title: "Call Us Directly",
                  val: "+91 63011 95478",
                  sub: "Available 24/7 for emergencies",
                },
                {
                  icon: Calendar,
                  title: "Walk-In Welcome",
                  val: "No prior appointment needed",
                  sub: "Especially for emergency cases",
                },
                {
                  icon: Stethoscope,
                  title: "Same Day Reports",
                  val: "Most reports ready in hours",
                  sub: "Digital & printed copies provided",
                },
              ].map(({ icon: Icon, title, val, sub }) => (
                <motion.div
                  key={title}
                  variants={fadeUp}
                  className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-primary-100 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800 text-sm mb-0.5">
                      {title}
                    </div>
                    <div className="text-gray-900 font-medium text-sm">
                      {val}
                    </div>
                    <div className="text-gray-400 text-xs mt-0.5">{sub}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10"
          >
            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -20 }}
                  onSubmit={submit}
                  className="space-y-5"
                >
                  <h3 className="font-poppins font-bold text-2xl text-gray-900 mb-2">
                    Request Appointment
                  </h3>
                  <p className="text-gray-400 text-sm mb-6">
                    Fill the form below and we'll call you back to confirm.
                  </p>

                  {/* Name */}
                  <div className="relative">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handle}
                        onFocus={() => setFocused("name")}
                        onBlur={() => setFocused("")}
                        placeholder="Enter your full name"
                        className={`${inputClass("name")} pl-10`}
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={form.phone}
                        onChange={handle}
                        onFocus={() => setFocused("phone")}
                        onBlur={() => setFocused("")}
                        placeholder="+91 XXXXX XXXXX"
                        className={`${inputClass("phone")} pl-10`}
                      />
                    </div>
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Preferred Date
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="date"
                        name="date"
                        value={form.date}
                        onChange={handle}
                        onFocus={() => setFocused("date")}
                        onBlur={() => setFocused("")}
                        min={new Date().toISOString().split("T")[0]}
                        className={`${inputClass("date")} pl-10`}
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Additional Notes
                    </label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={handle}
                      onFocus={() => setFocused("notes")}
                      onBlur={() => setFocused("")}
                      rows={3}
                      placeholder="Any specific requirements or doctor's referral..."
                      className={`${inputClass("notes")} resize-none`}
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm text-center bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                      {error}
                    </p>
                  )}

                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: loading ? 1 : 1.02 }}
                    whileTap={{ scale: loading ? 1 : 0.97 }}
                    className="w-full btn-primary justify-center text-base py-4 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <svg
                          className="animate-spin w-4 h-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8z"
                          />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Confirm Appointment <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </motion.button>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                    className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6"
                  >
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </motion.div>
                  <h3 className="font-poppins font-bold text-2xl text-gray-900 mb-3">
                    Appointment Requested!
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Thank you, <strong>{form.name}</strong>. We'll call you on{" "}
                    <strong>{form.phone}</strong> to confirm your appointment
                    shortly.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({
                        name: "",
                        phone: "",
                        service: "",
                        date: "",
                        notes: "",
                      });
                    }}
                    className="btn-outline"
                  >
                    Book Another
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
